import {
  Activity, BookOpen, Bot, Brain, CandlestickChart, Compass, Crosshair, Database, FlaskConical, Gauge, LayoutDashboard, Layers,
  Settings as SettingsIcon, SlidersHorizontal, Sparkles, Wrench,
} from "lucide-react";

/*
 * One navigation model, consumed by the sidebar, the command palette and the
 * page breadcrumb — so a route added here shows up everywhere at once.
 */
export const NAV_GROUPS = [
  {
    id: "trading",
    label: "Trading",
    roles: ["admin", "user"],
    items: [
      {
        path: "/",
        label: "Dashboard",
        icon: LayoutDashboard,
        description: "Overview of positions, P&L and market activity",
      },
      {
        path: "/decide",
        label: "Decide",
        icon: Compass,
        description: "What to buy right now and why: your rules' state, strikes side by side, a what-if grid — you place the order yourself",
      },
      {
        path: "/autotrader",
        label: "Autotrader",
        icon: Bot,
        roles: ["admin", "user"],
        description: "Enable/disable auto trading, live decisions, positions and kill switch",
      },
      {
        path: "/manual-trader",
        label: "Manual Trader",
        icon: Crosshair,
        roles: ["admin", "user"],
        description: "Place trades yourself — SL, trailing stop, target and exit rules are then managed automatically",
      },
      {
        path: "/algo-ai",
        label: "Algo AI",
        icon: Sparkles,
        roles: ["admin", "user"],
        description: "algo_ai's OI-wall probability + AI sentiment strategy, automated on every closed candle in paper",
      },
    ],
  },
  {
    id: "research",
    label: "Research",
    roles: ["admin"],
    items: [
      {
        path: "/rules",
        label: "Rule Builder",
        icon: Wrench,
        description: "Create and store trading rules: conditions or catalog strategies, contract, exits, sizing",
      },
      {
        path: "/rules/tutorial",
        label: "Rule Tutorial",
        icon: BookOpen,
        description: "How to write a rule: indicator units, typical values, CALL and PUT examples, mirroring, exits and common mistakes",
      },
      {
        path: "/rules-lab",
        label: "Rule Backtest",
        icon: SlidersHorizontal,
        description: "Run a saved rule on real option premiums and inspect the diagnostic dashboard",
      },
      {
        path: "/garch-range",
        label: "GARCH Range",
        icon: Gauge,
        description: "Expected NIFTY range for the next session, week and month from a GARCH(1,1) volatility model, with a walk-forward accuracy replay",
      },
      {
        path: "/garch-fences",
        label: "GARCH Fences",
        icon: CandlestickChart,
        description: "Textbook GARCH(1,1) on NIFTY daily closes: TradingView-style candles with tomorrow's range drawn as two fences every evening, and a day-by-day replay",
      },
      {
        path: "/structure-regime",
        label: "Regime & Structure",
        icon: Activity,
        description: "EMA / Bollinger / RSI / ADX regime label on 5-minute spot, live or replayed on any stored session, with how the label did over a date range — information only",
      },
      {
        path: "/oi-fib",
        label: "OI Profile & Fib",
        icon: Layers,
        description: "Candles with Auto Fib levels and call / put open interest per strike at the right edge, with max pain and OI walls — live or any stored session, information only",
      },
      {
        path: "/ml",
        label: "ML Models",
        icon: Brain,
        description: "mlmarket_dev ML Models: train XGBoost / LightGBM, deploy or retire, model health, performance and predictions",
      },
      {
        path: "/backtests",
        label: "ML Backtest",
        icon: FlaskConical,
        description: "mlmarket_dev F&O backtest: ML signal → options strategy → premium P&L, with real option candles where stored",
      },
    ],
  },
  {
    id: "data",
    label: "Data",
    roles: ["admin"],
    items: [
      {
        path: "/data",
        label: "Collection",
        icon: Database,
        description: "Manage data ingestion and market feed connections",
      },
    ],
  },
  {
    id: "system",
    label: "System",
    roles: ["admin"],
    items: [
      {
        path: "/settings",
        label: "Settings",
        icon: SettingsIcon,
        description: "Broker accounts, trading mode, risk limits and runtime configuration",
      },
    ],
  },
];

export const NAV_ITEMS = NAV_GROUPS.flatMap((group) =>
  group.items.map((item) => ({ ...item, group: group.label, groupRoles: group.roles })),
);

const allows = (roles, role) => !roles || roles.includes(role);

export function visibleGroups(role = "admin") {
  return NAV_GROUPS.filter((group) => allows(group.roles, role))
    .map((group) => ({ ...group, items: group.items.filter((item) => allows(item.roles, role)) }))
    .filter((group) => group.items.length > 0);
}

export function visibleItems(role = "admin") {
  return NAV_ITEMS.filter((item) => allows(item.groupRoles, role) && allows(item.roles, role));
}

export function findNavItem(pathname) {
  return NAV_ITEMS.find((item) => item.path === pathname);
}
